import { useContext } from 'react';
import '../../css/SideMenus/SideMenu.css';
import andexChartIcon from '../../images/sideMenu/andex-chart-icon.svg';
import andexChartIconBlack from '../../images/sideMenu/andex-chart-icon-black.svg';
import chartPercentageIcon from '../../images/sideMenu/tabler_percentage.svg';
import chartCalendarIcon from '../../images/sideMenu/tabler_calendar-repeat.svg';
import MenuBlock from './MenuBlock';
import { EI_PAGES, PAGE_TYPE, FABRIC_REPORTS, FILE_DOWNLOAD_PAGES, REPORT_GROUPS } from '../../constants';
import { MenuContext } from '../../context/MenuContext';
import { ReportContext } from '../../context/ReportContext';
import { ThemeContext } from '../../context/ThemeContext';
import { CircularProgress } from '@mui/material';
import ErrorCard from '../Cards/ErrorCard';
import { lookupToArray } from '../../helpers';

const SideMenu = () => {
    const { isMenuOpen, clientConsoleAccess, errorText, claimsRequestInFlight } =
        useContext(MenuContext);

    const { onOffCobrands, reportsMeta } = useContext(ReportContext);

    const { logoLarge, logoSmall } = useContext(ThemeContext);

    const andexTitleIcon = (
        <img src={andexChartIcon} style={{ color: '#000' }} alt="Rolling Periods" />
    );

    const andexItems = [
        {
            slug: EI_PAGES.fullAndex,
            name: 'Full ANDEX®',
            icon: <img src={andexChartIconBlack} alt="" />,
            pageType: PAGE_TYPE.educatedInvestor,
        },
        {
            slug: EI_PAGES.percentageReturns,
            name: 'Percentage returns',
            icon: <img src={chartPercentageIcon} alt="" />,
            pageType: PAGE_TYPE.educatedInvestor,
        },
        {
            slug: EI_PAGES.past30Years,
            name: 'Past 30 years',
            icon: <img src={chartCalendarIcon} alt="" />,
            pageType: PAGE_TYPE.educatedInvestor,
        },
    ];

    const isProdApiBaseUrl = (() => {
        try {
            const { hostname } = new URL(process.env.REACT_APP_API_BASE_URL || '');
            return hostname === 'api.igrad.com';
        } catch {
            return false;
        }
    })();

    const featureFlagFabricReport =
        process.env.REACT_APP_ENABLE_FEATURE_FLAGS === 'true' &&
        process.env.REACT_APP_FF_FABRIC_REPORTS === 'true' &&
        !isProdApiBaseUrl;

    if (errorText) {
        return (
            <div class="m-3">
                <ErrorCard>{errorText}</ErrorCard>
            </div>
        );
    }

    // Report items filtering / decorating
    const userHasPermissionToSeeReportItem = (item) => {
        // If on/off data failed to load, fall back to showing the menu so users can still access reports
        if (!onOffCobrands || onOffCobrands.length === 0) return true;

        // Iterate through onOffCobrands and allow it to be visible
        // so long at least one cobrand has it on
        if (onOffCobrands.every((x) => !x.reportSlugs.includes(item.slug))) {
            return false;
        }

        // User has access to user detail role
        if (
            !clientConsoleAccess.reportsUserDetailedEnabled &&
            item.detailLevel === 'user-detail'
        ) {
            return false;
        }

        return true;
    };

    // Build grouped report sections from REPORT_GROUPS config
    const allGroupedSlugs = new Set(REPORT_GROUPS.flatMap((g) => g.reports.map((r) => r.slug)));

    const reportGroups = REPORT_GROUPS.map((group) => {
        const items = group.reports
            .map((entry) => {
                const meta = reportsMeta[entry.slug];
                if (!meta) return null;
                return {
                    ...meta,
                    name: entry.displayName || meta.name,
                    icon: <img src={andexChartIconBlack} alt="" />,
                };
            })
            .filter((item) => item && userHasPermissionToSeeReportItem(item));

        return { ...group, items };
    });

    // Collect any reports not in a defined group into an "Other" fallback section
    const ungroupedItems = lookupToArray(reportsMeta)
        .filter((item) => !allGroupedSlugs.has(item.slug))
        .filter(userHasPermissionToSeeReportItem)
        .map((x) => ({ ...x, icon: <img src={andexChartIconBlack} alt="" /> }));

    if (featureFlagFabricReport) {
        // Append Fabric report to the Assessments group
        const assessmentsGroup = reportGroups.find((g) => g.key === 'assessments');
        if (assessmentsGroup) {
            assessmentsGroup.items.push({
                id: FABRIC_REPORTS.financialWellnessChange,
                name: 'Financial Wellness Change (Fabric)',
                icon: <img src={andexChartIconBlack} alt="" />,
                pageType: PAGE_TYPE.fabricReports,
            });
        }
    }

    const fileDownloadsItems = [
        {
            slug: FILE_DOWNLOAD_PAGES.counselingReports,
            name: 'Counseling Reports',
            icon: <img src={andexChartIconBlack} alt="" />,
            pageType: PAGE_TYPE.fileDownloads,
        },
    ];

    const noAccess =
        !claimsRequestInFlight &&
        !clientConsoleAccess?.educatedInvestorEnabled &&
        !clientConsoleAccess?.reportsEnabled &&
        !clientConsoleAccess?.hasActiveCounselingReports;

    return (
        <div className={`sidebar ${isMenuOpen ? 'expanded' : 'collapsed'}`}>
            <div className="sidebar-logo-container">
                <div className="flex justify-center my-4">
                    {isMenuOpen && logoLarge && (
                        <img
                            className="sidebar-logo-expanded"
                            src={`https://media-cdn.igrad.com/${logoLarge}`}
                            alt=""
                        />
                    )}
                    {!isMenuOpen && !logoSmall && (
                        <p>
                            <strong>CC</strong>
                        </p>
                    )}
                    {!isMenuOpen && logoSmall && (
                        <img
                            className="sidebar-logo-collapsed"
                            src={`https://media-cdn.igrad.com/${logoSmall}`}
                            alt=""
                        />
                    )}
                </div>
            </div>

            <div className="sidebar-nav-scroll">
                {noAccess && <p>No access</p>}

                {claimsRequestInFlight && (
                    <div className="flex justify-center">
                        <CircularProgress />
                    </div>
                )}

                {clientConsoleAccess?.educatedInvestorEnabled && (
                    <MenuBlock
                        title="ANDEX® Chart"
                        titleIcon={andexTitleIcon}
                        items={andexItems}
                        rootClass={'my-3'}
                        defaultExpanded={true}
                    />
                )}

                {clientConsoleAccess?.reportsEnabled &&
                    reportGroups.map(
                        (group) =>
                            group.items.length > 0 && (
                                <MenuBlock
                                    key={group.key}
                                    title={group.title}
                                    titleIcon={andexTitleIcon}
                                    items={group.items}
                                    rootClass={'my-3'}
                                    defaultExpanded={group.key === 'dashboards'}
                                />
                            )
                    )}

                {clientConsoleAccess?.reportsEnabled &&
                    ungroupedItems.length > 0 && (
                        <MenuBlock
                            title="Other"
                            titleIcon={andexTitleIcon}
                            items={ungroupedItems}
                            rootClass={'my-3'}
                        />
                    )}

                {clientConsoleAccess?.hasActiveCounselingReports && (
                    <MenuBlock
                        title="File Downloads"
                        titleIcon={andexTitleIcon}
                        items={fileDownloadsItems}
                        rootClass={'my-3'}
                    />
                )}
            </div>
        </div>
    );
};

export default SideMenu;
